// AuthContext.js
import { createContext, useEffect, useState, useCallback } from 'react';
import { logoutUser, refreshAccessToken } from '../../api/authHelpers';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [authState, setAuthState] = useState(() => {
    // Initialize state from localStorage once
    const accessToken = localStorage.getItem('accessToken');
    const role = localStorage.getItem('userRole');
    const userId = localStorage.getItem('userId');
    const accessTokenExpiresAt = localStorage.getItem('accessTokenExpiresAt');
    
    return {
      isAuthenticated: !!accessToken && !!role && !!userId,
      role,
      userId,
      accessTokenExpiresAt: accessTokenExpiresAt ? parseInt(accessTokenExpiresAt) : null
    };
  });

  const checkAndRefreshToken = useCallback(() => {
    const accessTokenExpiresAt = localStorage.getItem('accessTokenExpiresAt');
    if (!accessTokenExpiresAt) return;

    const expiryTime = parseInt(accessTokenExpiresAt) * 1000; // Convert to milliseconds
    const now = new Date().getTime();
    const timeUntilExpiry = expiryTime - now;

    // Refresh token if it will expire in less than 30 minutes
    if (timeUntilExpiry < 30 * 60 * 1000) {
      refreshAccessToken().catch(error => {
        console.error('Failed to refresh token:', error);
        logout();
      });
    }
  }, []);

  const verifyAuth = useCallback(() => {
    const accessToken = localStorage.getItem('accessToken');
    const role = localStorage.getItem('userRole');
    const userId = localStorage.getItem('userId');
    const accessTokenExpiresAt = localStorage.getItem('accessTokenExpiresAt');
    
    const isAuth = !!accessToken && !!role && !!userId;
    
    setAuthState(prev => {
      if (prev.isAuthenticated !== isAuth || 
          prev.role !== role || 
          prev.userId !== userId ||
          prev.accessTokenExpiresAt !== accessTokenExpiresAt) {
        return { 
          isAuthenticated: isAuth, 
          role, 
          userId,
          accessTokenExpiresAt: accessTokenExpiresAt ? parseInt(accessTokenExpiresAt) : null
        };
      }
      return prev;
    });
    
    return { 
      isAuthenticated: isAuth, 
      role, 
      userId,
      accessTokenExpiresAt: accessTokenExpiresAt ? parseInt(accessTokenExpiresAt) : null
    };
  }, []);

  // Set up periodic check every 15 minutes
  useEffect(() => {
    // Initial check
    checkAndRefreshToken();
    // Set up interval for periodic checks
    const intervalId = setInterval(() => {
      checkAndRefreshToken();
    }, 15 * 60 * 1000); // 15 minutes
    
    return () => clearInterval(intervalId);
  }, [checkAndRefreshToken]);

  // Listen for storage changes
  useEffect(() => {
    const handleStorageChange = () => verifyAuth();
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [verifyAuth]);

  const login = () => {
    // Logic for login
    verifyAuth();
  };

  const logout = () => {
    // Logic for logout
    localStorage.clear();
    setAuthState({
      isAuthenticated: false,
      role: null,
      userId: null,
      accessTokenExpiresAt: null
    });
    logoutUser();
  };

  return (
    <AuthContext.Provider value={{ ...authState, login, logout, verifyAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
